import { Box, Link, Typography } from "@mui/material";
import { keyframes, type Theme } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";

/**
 * Strategist's monthly TL;DR. Sits to the right of the AgentHero on
 * lg+ as the 2/5 column of the focal grid; collapses below it on
 * smaller viewports. Same tracking-wide label rhythm as the hero so
 * the pair reads as a single composition.
 */

// One-shot entrance (was the `.rise-in` utility in globals.css). The
// memo enters 60 ms after the hero for a single orchestrated reveal.
const riseIn = keyframes({
  from: { opacity: 0, transform: "translateY(6px)" },
  to: { opacity: 1, transform: "translateY(0)" },
});

const riseInSx = {
  animation: `${riseIn} 460ms cubic-bezier(0.16,1,0.3,1) both`,
  animationDelay: "60ms",
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
} as const;

const shortDate = new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short" });

/** A memo is out of date once its 30-day cycle has run out. Exported for the
 * unit test; `now` is injectable for the same reason. */
export function isMemoStale(expiresAt: string | null | undefined, now: number = Date.now()): boolean {
  if (!expiresAt) return false;
  const t = Date.parse(expiresAt);
  return Number.isFinite(t) && t <= now;
}

export function MissionBanner({
  tldr,
  writtenAt = null,
  expiresAt = null,
}: {
  tldr: string | null;
  /** When the strategist wrote this memo (ISO). */
  writtenAt?: string | null;
  /** When its 30-day cycle ends (ISO). Past it, the card says so instead of
   * calling the memo "This month" — 2026-09-23 it showed August's plan in
   * late September. */
  expiresAt?: string | null;
}): JSX.Element {
  const stale = isMemoStale(expiresAt);
  const written = writtenAt && Number.isFinite(Date.parse(writtenAt))
    ? shortDate.format(new Date(writtenAt))
    : null;
  if (!tldr) {
    return (
      <Box
        component="section"
        aria-label="This month's mission"
        sx={{
          display: "flex",
          height: "100%",
          flexDirection: "column",
          gap: 1.5,
          borderRadius: 1,
          border: 1,
          borderStyle: "dashed",
          borderColor: "divider",
          bgcolor: "surfaceSoft",
          p: 2.5,
          "@container (min-width:28rem)": { p: 3 },
          ...riseInSx,
        }}
      >
        <Typography component="h2" sx={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.16em", color: "text.disabled" }}>
          Monthly memo
        </Typography>
        <Typography sx={{ fontSize: 14, lineHeight: 1.6, color: "text.secondary" }}>
          The strategist hasn't written its first memo yet — usually appears within
          an hour after the company-self-knowledge bootstrap finishes. Scroll down
          to <strong>“What the agent is doing right now”</strong> to see what's
          running. {/* Was a link to the Brain tab; that tab folded into the
          bottom of THIS page 2026-08-07, so a route link would be a self-link. */}
        </Typography>
      </Box>
    );
  }
  return (
    <Box
      component="section"
      aria-label="This month's mission"
      sx={{
        position: "relative",
        display: "flex",
        height: "100%",
        flexDirection: "column",
        gap: 1,
        overflow: "hidden",
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: (t: Theme) => t.shadows[1],
        p: 2,
        containerType: "inline-size",
        "@container (min-width:28rem)": { p: 2.5 },
        ...riseInSx,
      }}
    >
      <Box component="header" sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1.5 }}>
        <Typography
          component="h2"
          data-testid="mission-banner-title"
          sx={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.16em", color: stale ? "warning.main" : "text.disabled" }}
        >
          {stale ? "Out of date" : "This month"}
        </Typography>
        <Box component="span" data-testid="mission-banner-written" sx={{ fontSize: 10, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.14em", color: "text.disabled" }}>
          {written ? `Strategist memo · ${written}` : "Strategist memo"}
        </Box>
      </Box>
      {stale ? (
        <Typography data-testid="mission-banner-stale" sx={{ fontSize: 12, color: "text.secondary" }}>
          This memo's 30-day cycle has ended, so it may not describe what the agent is hunting now.
        </Typography>
      ) : null}
      {/* The full strategy lives behind the link below — clamp the
       * preview so this card's height stays roughly matched to the
       * compact AgentHero on its left. */}
      <Typography
        sx={{
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textWrap: "pretty",
          fontSize: "0.8125rem",
          lineHeight: 1.55,
          color: "text.primary",
          "@container (min-width:28rem)": { fontSize: "0.875rem" },
        }}
      >
        {tldr}
      </Typography>
      <Box component="footer" sx={{ mt: "auto", display: "flex", alignItems: "center", justifyContent: "flex-end", pt: 0.5 }}>
        <Link
          component={RouterLink}
          to="/sales/strategy"
          underline="hover"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            fontSize: 12,
            fontWeight: 500,
            color: "primary.main",
            "&:hover .arrow": { transform: "translateX(2px)" },
          }}
        >
          Read full strategy
          <Box
            component="span"
            className="arrow"
            aria-hidden
            sx={{ transition: "transform 200ms ease-out" }}
          >
            →
          </Box>
        </Link>
      </Box>
    </Box>
  );
}
